import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Breadcrumb, type BreadcrumbItem, Spinner } from '../../../shared/components';
import { useToast } from '../../notifications';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionCodes } from '../../auth/permissions';
import {
  deleteKnowledgeDocument,
  listKnowledgeDocuments,
  listKnowledgeFileActivity,
  listKnowledgeRetrievalLogs,
  reprocessKnowledgeDocument,
  retrieveKnowledge,
  submitKnowledgeRetrievalFeedback,
  updateKnowledgeDocumentSummary,
  updateKnowledgeDocumentTags,
  updateKnowledgeDocumentTitle,
  updateKnowledgeDocumentVisibility,
  uploadKnowledgeDocument,
  uploadKnowledgeDocumentAttachment,
  getKnowledgeAttachmentSettings,
  listKnowledgeDataCategories,
  replaceKnowledgeDocumentFile,
} from '../services/knowledgeService';
import {
  DEFAULT_KNOWLEDGE_IMAGE_MIME_TYPES,
  DEFAULT_KNOWLEDGE_MAX_IMAGE_BYTES,
  DEFAULT_KNOWLEDGE_MAX_VIDEO_BYTES,
  DEFAULT_KNOWLEDGE_VIDEO_MIME_TYPES,
  KnowledgeAttachmentSettings,
  KnowledgeDataCategory,
  ChangeActivityItem,
  KnowledgeDocument,
  KnowledgeDocumentAccess,
  KnowledgeProcessingStatus,
  KnowledgeRetrievalContextType,
  KnowledgeRetrievalLog,
  KnowledgeRetrievedChunk,
  KnowledgeVisibility,
  KNOWLEDGE_UNCATEGORIZED_FILE_CATEGORY,
  getKnowledgeAttachmentAccept,
  isKnowledgeMediaFile,
  isKnowledgeVideoAttachment,
  isStructuredKnowledgeDocument,
} from '../types/Knowledge';
import { KnowledgeBaseOperationalSummaryPanel } from './KnowledgeBaseOperationalSummaryPanel';
import { KnowledgeBaseSettingsPanel } from './KnowledgeBaseSettingsPanel';
import { KnowledgeEvaluationPanel } from './KnowledgeEvaluationPanel';
import { KnowledgeDocumentCard } from './KnowledgeDocumentCard';
import { KnowledgeDocumentAccessModal } from './KnowledgeDocumentAccessModal';
import {
  KnowledgeDocumentGrid,
  KnowledgeDocumentTable,
  KnowledgeDocumentsViewMode,
  KnowledgeDocumentsViewToggle,
  isKnowledgeDocumentsViewMode,
} from './KnowledgeDocumentCompactViews';
import styles from './KnowledgeBasePage.module.css';

const PAGE_SIZE = 25;
const DOCUMENTS_VIEW_MODE_STORAGE_KEY = 'knowledge.documents.viewMode';

const readStoredViewMode = (): KnowledgeDocumentsViewMode => {
  try {
    const stored = window.localStorage.getItem(DOCUMENTS_VIEW_MODE_STORAGE_KEY);
    return isKnowledgeDocumentsViewMode(stored) ? stored : 'detailed';
  } catch {
    return 'detailed';
  }
};
const ACTIVITY_PAGE_SIZE = 25;
const RETRIEVAL_LOG_PAGE_SIZE = 50;

const DEFAULT_ATTACHMENT_SETTINGS: KnowledgeAttachmentSettings = {
  enabled: true,
  maxAttachmentsPerDocument: 20,
  maxAttachmentBytes: DEFAULT_KNOWLEDGE_MAX_IMAGE_BYTES,
  maxVideoAttachmentBytes: DEFAULT_KNOWLEDGE_MAX_VIDEO_BYTES,
  maxCaptionLength: 500,
  maxAltTextLength: 500,
  allowedImageMimeTypesCsv: DEFAULT_KNOWLEDGE_IMAGE_MIME_TYPES,
  allowedVideoMimeTypesCsv: DEFAULT_KNOWLEDGE_VIDEO_MIME_TYPES,
};

const processingStatuses: Array<KnowledgeProcessingStatus | 'all'> = [
  'all',
  'uploaded',
  'parsing',
  'chunking',
  'embedding',
  'ready',
  'failed',
];

const visibilityOptions: Array<KnowledgeVisibility | 'all'> = ['all', 'internal', 'customer_safe', 'public'];
const retrievalContextOptions: Array<KnowledgeRetrievalContextType | 'all'> = ['all', 'ai_reply', 'ai_draft', 'ai_summary'];

const getDefaultTitleFromFile = (file: File | null): string => {
  if (!file?.name) {
    return '';
  }

  const name = file.name.trim();
  const lastDotIndex = name.lastIndexOf('.');
  return lastDotIndex > 0 ? name.slice(0, lastDotIndex) : name;
};

const formatFileSize = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '0 B';
  }

  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  return `${value >= 10 || unitIndex === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[unitIndex]}`;
};

const isJsonUploadFile = (file: File | null): boolean => {
  if (!file) {
    return false;
  }

  const mimeType = file.type.toLowerCase();
  if (mimeType.includes('json')) {
    return true;
  }

  return file.name.toLowerCase().endsWith('.json');
};

const formatDateTime = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const formatContextType = (value: string | null | undefined): string => {
  switch (value) {
    case 'ai_reply':
      return 'AI reply';
    case 'ai_draft':
      return 'AI draft';
    case 'ai_summary':
      return 'AI summary';
    default:
      return value || 'Unknown';
  }
};

export const KnowledgeBasePage: React.FC = () => {
  const { t } = useTranslation(['knowledge', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const { hasPermission, isPlatformOperator } = useAuth();
  const canViewOperations = isPlatformOperator;
  const canManageKnowledge = hasPermission([PermissionCodes.knowledgeManage]);
  const urlQueryState = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    const search = (params.get('search') || '').trim();
    const sourceFile = (params.get('sourceFile') || '').trim();
    const sourceChunk = Number(params.get('sourceChunk') || 0);
    const retrieveQuery = (params.get('retrieveQuery') || sourceFile || search || '').trim();

    return {
      search,
      sourceFile,
      sourceChunk: Number.isFinite(sourceChunk) && sourceChunk > 0 ? sourceChunk : null,
      retrieveQuery,
    };
  }, [location.search]);

  const [documents, setDocuments] = React.useState<KnowledgeDocument[]>([]);
  const [page, setPage] = React.useState(1);
  const [totalCount, setTotalCount] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [searchDraft, setSearchDraft] = React.useState(urlQueryState.search);
  const [search, setSearch] = React.useState(urlQueryState.search);
  const [visibility, setVisibility] = React.useState<KnowledgeVisibility | 'all'>('all');
  const [status, setStatus] = React.useState<KnowledgeProcessingStatus | 'all'>('all');
  const [fileCategoryFilter, setFileCategoryFilter] = React.useState<string>('all');

  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [pendingUploadImages, setPendingUploadImages] = React.useState<File[]>([]);
  const [attachmentSettings, setAttachmentSettings] = React.useState<KnowledgeAttachmentSettings | null>(null);
  const [uploadTitle, setUploadTitle] = React.useState('');
  const [uploadVisibility, setUploadVisibility] = React.useState<KnowledgeVisibility>('internal');
  const [uploadTags, setUploadTags] = React.useState('');
  const [uploadSummary, setUploadSummary] = React.useState('');
  const [uploadFileCategory, setUploadFileCategory] = React.useState('');
  const [dataCategories, setDataCategories] = React.useState<KnowledgeDataCategory[]>([]);
  const [uploading, setUploading] = React.useState(false);
  const [isUploadImagesDropZoneActive, setIsUploadImagesDropZoneActive] = React.useState(false);
  const [busyDocumentId, setBusyDocumentId] = React.useState<string>('');
  const [accessDocument, setAccessDocument] = React.useState<KnowledgeDocument | null>(null);
  const [expandedAttachmentsDocumentId, setExpandedAttachmentsDocumentId] = React.useState<string | null>(null);
  const [editingTitleId, setEditingTitleId] = React.useState<string>('');
  const [titleDraft, setTitleDraft] = React.useState<string>('');
  const [editingSummaryId, setEditingSummaryId] = React.useState<string>('');
  const [summaryDraft, setSummaryDraft] = React.useState<string>('');
  const [editingTagsId, setEditingTagsId] = React.useState<string>('');
  const [tagsDraft, setTagsDraft] = React.useState<string>('');
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const uploadDialogRef = React.useRef<HTMLDivElement | null>(null);
  const uploadImagesInputRef = React.useRef<HTMLInputElement | null>(null);
  const [retrieveQuery, setRetrieveQuery] = React.useState(urlQueryState.retrieveQuery);
  const [retrieveAudience, setRetrieveAudience] = React.useState<'employee' | 'customer'>('employee');
  const [retrieveMaxChunks, setRetrieveMaxChunks] = React.useState(5);
  const [retrieving, setRetrieving] = React.useState(false);
  const [retrieveError, setRetrieveError] = React.useState<string | null>(null);
  const [retrieveChunks, setRetrieveChunks] = React.useState<KnowledgeRetrievedChunk[]>([]);
  const [retrievalLogs, setRetrievalLogs] = React.useState<KnowledgeRetrievalLog[]>([]);
  const [retrievalLogsPage, setRetrievalLogsPage] = React.useState(1);
  const [retrievalLogsTotalCount, setRetrievalLogsTotalCount] = React.useState(0);
  const [retrievalLogsLoading, setRetrievalLogsLoading] = React.useState(false);
  const [retrievalLogsError, setRetrievalLogsError] = React.useState<string | null>(null);
  const [retrievalContextFilter, setRetrievalContextFilter] = React.useState<KnowledgeRetrievalContextType | 'all'>('all');
  const [feedbackDrafts, setFeedbackDrafts] = React.useState<Record<string, string>>({});
  const [savingFeedbackLogId, setSavingFeedbackLogId] = React.useState<string>('');
  const [fileActivity, setFileActivity] = React.useState<ChangeActivityItem[]>([]);
  const [fileActivityPage, setFileActivityPage] = React.useState(1);
  const [fileActivityTotalCount, setFileActivityTotalCount] = React.useState(0);
  const [fileActivityLoading, setFileActivityLoading] = React.useState(false);
  const [fileActivityError, setFileActivityError] = React.useState<string | null>(null);
  const [fileActivityAction, setFileActivityAction] = React.useState<'all' | 'created' | 'updated' | 'deleted'>('all');
  const [fileActivityDocumentId, setFileActivityDocumentId] = React.useState('');
  const [fileActivityDocumentLabel, setFileActivityDocumentLabel] = React.useState('');
  const [activeTab, setActiveTab] = React.useState<'documents' | 'activity' | 'retrieve' | 'logs' | 'operations'>('documents');
  const [uploadPanelOpen, setUploadPanelOpen] = React.useState(false);
  const [documentsViewMode, setDocumentsViewMode] = React.useState<KnowledgeDocumentsViewMode>(readStoredViewMode);
  const [focusedDocumentId, setFocusedDocumentId] = React.useState<string | null>(null);
  const isStructuredJsonUpload = isJsonUploadFile(selectedFile);

  const changeDocumentsViewMode = React.useCallback((mode: KnowledgeDocumentsViewMode) => {
    setDocumentsViewMode(mode);
    try {
      window.localStorage.setItem(DOCUMENTS_VIEW_MODE_STORAGE_KEY, mode);
    } catch {
      // Preference persistence is best-effort; the view still switches for this session.
    }
  }, []);

  // Compact views only show essentials; "Details" jumps to the full card where editing lives.
  const openDocumentDetails = React.useCallback((document: KnowledgeDocument) => {
    setFocusedDocumentId(document.id);
    changeDocumentsViewMode('detailed');
  }, [changeDocumentsViewMode]);

  React.useEffect(() => {
    if (!focusedDocumentId || documentsViewMode !== 'detailed') {
      return;
    }

    const element = window.document.getElementById(`knowledge-document-${focusedDocumentId}`);
    element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = window.setTimeout(() => setFocusedDocumentId(null), 2000);
    return () => window.clearTimeout(timer);
  }, [focusedDocumentId, documentsViewMode]);
  const effectiveAttachmentSettings = attachmentSettings ?? DEFAULT_ATTACHMENT_SETTINGS;
  const attachmentsFeatureEnabled = effectiveAttachmentSettings.enabled !== false;

  const addPendingUploadImages = React.useCallback((files: File[]) => {
    const mediaFiles = files.filter(isKnowledgeMediaFile);
    if (mediaFiles.length === 0) {
      return;
    }

    const maxCount = effectiveAttachmentSettings.maxAttachmentsPerDocument ?? 20;
    setPendingUploadImages(current => {
      const merged = [...current, ...mediaFiles];
      return merged.slice(0, maxCount);
    });
  }, [effectiveAttachmentSettings.maxAttachmentsPerDocument]);

  const breadcrumbItems: BreadcrumbItem[] = React.useMemo(() => ([
    { label: t('knowledgeBase', { ns: 'knowledge' }), path: '/ai/knowledge' },
  ]), [t]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const retrievalLogTotalPages = Math.max(1, Math.ceil(retrievalLogsTotalCount / RETRIEVAL_LOG_PAGE_SIZE));

  const getFileCategoryLabel = React.useCallback((fileCategory: string | null | undefined) => {
    if (!fileCategory?.trim()) {
      return '—';
    }

    const category = dataCategories.find(item => item.categoryId === fileCategory);
    return category?.displayName ?? fileCategory;
  }, [dataCategories]);

  const loadDataCategories = React.useCallback(async () => {
    try {
      const categories = await listKnowledgeDataCategories();
      setDataCategories(categories);
    } catch {
      setDataCategories([]);
    }
  }, []);

  const loadDocuments = React.useCallback(async (options?: { silent?: boolean }) => {
    const silent = options?.silent === true;

    if (!silent) {
      setLoading(true);
    }

    setError(null);

    try {
      const result = await listKnowledgeDocuments({
        page,
        pageSize: PAGE_SIZE,
        search,
        visibility,
        status,
        fileCategory: fileCategoryFilter,
      });

      setDocuments(result.items);
      setTotalCount(result.totalCount);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : t('failedToLoad', { ns: 'knowledge' }));
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [fileCategoryFilter, page, search, status, t, visibility]);

  React.useEffect(() => {
    setSearchDraft(urlQueryState.search);
    setSearch(urlQueryState.search);
    setRetrieveQuery(urlQueryState.retrieveQuery);
    setPage(1);
  }, [urlQueryState.retrieveQuery, urlQueryState.search]);

  React.useEffect(() => {
    void loadDocuments();
  }, [loadDocuments]);

  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = searchDraft.trim();
      setSearch(prev => {
        if (prev === next) {
          return prev;
        }

        setPage(1);
        return next;
      });
    }, 350);

    return () => window.clearTimeout(timer);
  }, [searchDraft]);

  React.useEffect(() => {
    void loadDataCategories();
  }, [loadDataCategories]);

  React.useEffect(() => {
    if (!canManageKnowledge) {
      return;
    }

    void getKnowledgeAttachmentSettings()
      .then(setAttachmentSettings)
      .catch(() => setAttachmentSettings(DEFAULT_ATTACHMENT_SETTINGS));
  }, [canManageKnowledge]);

  React.useEffect(() => {
    if (isStructuredJsonUpload) {
      setPendingUploadImages([]);
    }
  }, [isStructuredJsonUpload]);

  const pendingImagePreviewUrls = React.useMemo(
    () => pendingUploadImages.map(file => URL.createObjectURL(file)),
    [pendingUploadImages],
  );

  React.useEffect(() => () => {
    pendingImagePreviewUrls.forEach(url => URL.revokeObjectURL(url));
  }, [pendingImagePreviewUrls]);

  const loadRetrievalLogs = React.useCallback(async () => {
    if (!canManageKnowledge) {
      return;
    }

    setRetrievalLogsLoading(true);
    setRetrievalLogsError(null);

    try {
      const result = await listKnowledgeRetrievalLogs({
        page: retrievalLogsPage,
        pageSize: RETRIEVAL_LOG_PAGE_SIZE,
        contextType: retrievalContextFilter,
      });

      setRetrievalLogs(result.items);
      setRetrievalLogsTotalCount(result.totalCount);
    } catch (loadError) {
      setRetrievalLogsError(loadError instanceof Error ? loadError.message : t('retrievalLogsFailedToLoad', { ns: 'knowledge' }));
    } finally {
      setRetrievalLogsLoading(false);
    }
  }, [canManageKnowledge, retrievalContextFilter, retrievalLogsPage, t]);

  React.useEffect(() => {
    void loadRetrievalLogs();
  }, [loadRetrievalLogs]);

  const fileActivityTotalPages = Math.max(1, Math.ceil(fileActivityTotalCount / ACTIVITY_PAGE_SIZE));

  const loadFileActivity = React.useCallback(async () => {
    if (activeTab !== 'activity') {
      return;
    }

    setFileActivityLoading(true);
    setFileActivityError(null);

    try {
      const result = await listKnowledgeFileActivity({
        page: fileActivityPage,
        pageSize: ACTIVITY_PAGE_SIZE,
        documentId: fileActivityDocumentId,
        action: fileActivityAction,
      });
      setFileActivity(result.items);
      setFileActivityTotalCount(result.totalCount);
    } catch (loadError) {
      setFileActivityError(loadError instanceof Error ? loadError.message : t('activity.failedToLoad', { ns: 'knowledge' }));
    } finally {
      setFileActivityLoading(false);
    }
  }, [activeTab, fileActivityAction, fileActivityDocumentId, fileActivityPage, t]);

  React.useEffect(() => {
    void loadFileActivity();
  }, [loadFileActivity]);

  const onRefresh = () => {
    void loadDocuments();
    void loadRetrievalLogs();
    void loadFileActivity();
  };

  const onViewFileActivity = (document: KnowledgeDocument) => {
    setFileActivityDocumentId(document.id);
    setFileActivityDocumentLabel(document.title || document.originalFileName);
    setFileActivityPage(1);
    setActiveTab('activity');
  };

  const closeUploadModal = () => {
    if (uploading) {
      return;
    }

    setUploadPanelOpen(false);
  };

  React.useEffect(() => {
    if (!uploadPanelOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    uploadDialogRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [uploadPanelOpen]);

  React.useEffect(() => {
    if (!uploadPanelOpen) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !uploading) {
        setUploadPanelOpen(false);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [uploadPanelOpen, uploading]);

  const onToggleDocumentAttachments = (documentId: string) => {
    setExpandedAttachmentsDocumentId(current => (current === documentId ? null : documentId));
  };

  const onUpload = async () => {
    if (!selectedFile) {
      showToast(t('selectFileFirst', { ns: 'knowledge' }), 'error');
      return;
    }

    if (isStructuredJsonUpload && !uploadFileCategory.trim()) {
      showToast(t('fileCategoryRequired', { ns: 'knowledge' }), 'error');
      return;
    }

    setUploading(true);
    try {
      const created = await uploadKnowledgeDocument({
        file: selectedFile,
        title: uploadTitle,
        visibility: uploadVisibility,
        tags: uploadTags,
        summary: uploadSummary,
        fileCategory: isStructuredJsonUpload ? uploadFileCategory : undefined,
      });

      if (!isStructuredJsonUpload && pendingUploadImages.length > 0 && attachmentsFeatureEnabled) {
        for (const image of pendingUploadImages) {
          await uploadKnowledgeDocumentAttachment(created.id, { file: image });
        }
      }

      setSelectedFile(null);
      setPendingUploadImages([]);
      setUploadTitle('');
      setUploadTags('');
      setUploadSummary('');
      setUploadFileCategory('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      if (uploadImagesInputRef.current) {
        uploadImagesInputRef.current.value = '';
      }

      const uploadedImages = !isStructuredJsonUpload ? pendingUploadImages.length : 0;
      showToast(
        isStructuredJsonUpload
          ? t('uploadSuccessStructured', { ns: 'knowledge' })
          : uploadedImages > 0
            ? t('uploadSuccessWithImages', { ns: 'knowledge', count: uploadedImages })
            : t('uploadSuccess', { ns: 'knowledge' }),
        'success',
      );
      setPage(1);
      setUploadPanelOpen(false);
      onRefresh();
    } catch (uploadError) {
      showToast(uploadError instanceof Error ? uploadError.message : t('failedToUpload', { ns: 'knowledge' }), 'error');
    } finally {
      setUploading(false);
    }
  };

  const onReprocess = async (documentId: string) => {
    setBusyDocumentId(documentId);
    try {
      await reprocessKnowledgeDocument(documentId);
      showToast(t('reprocessQueued', { ns: 'knowledge' }), 'success');
      onRefresh();
    } catch (reprocessError) {
      showToast(reprocessError instanceof Error ? reprocessError.message : t('failedToReprocess', { ns: 'knowledge' }), 'error');
    } finally {
      setBusyDocumentId('');
    }
  };

  const onReplaceFile = async (document: KnowledgeDocument, file: File) => {
    const confirmed = window.confirm(
      t('updateFileConfirm', { ns: 'knowledge', fileName: document.originalFileName }),
    );

    if (!confirmed) {
      return;
    }

    setBusyDocumentId(document.id);
    try {
      const updated = await replaceKnowledgeDocumentFile(document.id, file);
      setDocuments(current => current.map(item => (item.id === updated.id ? updated : item)));
      showToast(
        isStructuredKnowledgeDocument(updated)
          ? t('fileUpdated', { ns: 'knowledge' })
          : t('fileUpdatedAndReprocessQueued', { ns: 'knowledge' }),
        'success',
      );
      onRefresh();
    } catch (replaceError) {
      showToast(replaceError instanceof Error ? replaceError.message : t('failedToUpdateFile', { ns: 'knowledge' }), 'error');
    } finally {
      setBusyDocumentId('');
    }
  };

  const onChangeVisibility = async (document: KnowledgeDocument, newVisibility: KnowledgeVisibility) => {
    setBusyDocumentId(document.id);
    try {
      const updated = await updateKnowledgeDocumentVisibility(document.id, newVisibility);
      setDocuments(current => current.map(d => d.id === updated.id ? updated : d));
      showToast(t('visibilityUpdated', { ns: 'knowledge' }), 'success');
    } catch (changeError) {
      showToast(changeError instanceof Error ? changeError.message : t('failedToUpdateVisibility', { ns: 'knowledge' }), 'error');
    } finally {
      setBusyDocumentId('');
    }
  };

  const onOpenAccess = (document: KnowledgeDocument) => {
    setAccessDocument(document);
  };

  const onAccessSaved = (access: KnowledgeDocumentAccess) => {
    setDocuments(current => current.map(d => (
      d.id === access.documentId ? { ...d, isAccessRestricted: access.isRestricted } : d
    )));
    setAccessDocument(null);
  };

  const onStartEditSummary = (document: KnowledgeDocument) => {
    setEditingSummaryId(document.id);
    setSummaryDraft(document.summary ?? '');
  };

  const onStartEditTitle = (document: KnowledgeDocument) => {
    setEditingTitleId(document.id);
    setTitleDraft(document.title);
  };

  const onCancelEditTitle = () => {
    setEditingTitleId('');
    setTitleDraft('');
  };

  const onSaveTitle = async (documentId: string) => {
    setBusyDocumentId(documentId);
    try {
      const updated = await updateKnowledgeDocumentTitle(documentId, { title: titleDraft });
      setDocuments(current => current.map(d => d.id === updated.id ? updated : d));
      setEditingTitleId('');
      setTitleDraft('');
      showToast(t('titleUpdated', { ns: 'knowledge' }), 'success');
    } catch (saveError) {
      showToast(saveError instanceof Error ? saveError.message : t('failedToUpdateTitle', { ns: 'knowledge' }), 'error');
    } finally {
      setBusyDocumentId('');
    }
  };

  const onCancelEditSummary = () => {
    setEditingSummaryId('');
    setSummaryDraft('');
  };

  const onSaveSummary = async (documentId: string) => {
    setBusyDocumentId(documentId);
    try {
      const updated = await updateKnowledgeDocumentSummary(documentId, { summary: summaryDraft });
      setDocuments(current => current.map(d => d.id === updated.id ? updated : d));
      setEditingSummaryId('');
      setSummaryDraft('');
      showToast(t('summaryUpdated', { ns: 'knowledge' }), 'success');
    } catch (saveError) {
      showToast(saveError instanceof Error ? saveError.message : t('failedToUpdateSummary', { ns: 'knowledge' }), 'error');
    } finally {
      setBusyDocumentId('');
    }
  };

  const onStartEditTags = (document: KnowledgeDocument) => {
    setEditingTagsId(document.id);
    setTagsDraft(document.tags.join(', '));
  };

  const onCancelEditTags = () => {
    setEditingTagsId('');
    setTagsDraft('');
  };

  const onSaveTags = async (documentId: string) => {
    if (!documentId.trim()) {
      showToast(t('failedToUpdateTags', { ns: 'knowledge' }), 'error');
      return;
    }

    setBusyDocumentId(documentId);
    try {
      const updated = await updateKnowledgeDocumentTags(documentId, { tags: tagsDraft });
      setDocuments(current => current.map(d => d.id === updated.id ? updated : d));
      setEditingTagsId('');
      setTagsDraft('');
      showToast(t('tagsUpdated', { ns: 'knowledge' }), 'success');
    } catch (saveError) {
      showToast(saveError instanceof Error ? saveError.message : t('failedToUpdateTags', { ns: 'knowledge' }), 'error');
    } finally {
      setBusyDocumentId('');
    }
  };

  const onAttachmentCountChange = React.useCallback((documentId: string, count: number) => {
    setDocuments(current => current.map(document => (
      document.id === documentId ? { ...document, attachmentCount: count } : document
    )));
  }, []);

  const onDelete = async (document: KnowledgeDocument) => {
    const confirmed = window.confirm(
      t('deleteConfirm', { ns: 'knowledge', fileName: document.originalFileName }),
    );

    if (!confirmed) {
      return;
    }

    setBusyDocumentId(document.id);
    try {
      await deleteKnowledgeDocument(document.id);
      showToast(t('deleteSuccess', { ns: 'knowledge' }), 'success');

      if (documents.length === 1 && page > 1) {
        setPage(current => current - 1);
      } else {
        onRefresh();
      }
    } catch (deleteError) {
      showToast(deleteError instanceof Error ? deleteError.message : t('failedToDelete', { ns: 'knowledge' }), 'error');
    } finally {
      setBusyDocumentId('');
    }
  };

  const onCopyPublicUrl = async (publicUrl: string) => {
    if (!publicUrl.trim()) {
      showToast(t('publicUrlUnavailable', { ns: 'knowledge' }), 'error');
      return;
    }

    if (!navigator.clipboard?.writeText) {
      showToast(t('publicUrlCopyUnsupported', { ns: 'knowledge' }), 'error');
      return;
    }

    try {
      await navigator.clipboard.writeText(publicUrl);
      showToast(t('publicUrlCopied', { ns: 'knowledge' }), 'success');
    } catch {
      showToast(t('publicUrlCopyFailed', { ns: 'knowledge' }), 'error');
    }
  };

  const onRetrieve = async (event: React.FormEvent) => {
    event.preventDefault();

    const normalizedQuery = retrieveQuery.trim();
    if (!normalizedQuery) {
      setRetrieveError(t('retrieveQueryRequired', { ns: 'knowledge' }));
      setRetrieveChunks([]);
      return;
    }

    setRetrieving(true);
    setRetrieveError(null);

    try {
      const result = await retrieveKnowledge({
        query: normalizedQuery,
        audience: retrieveAudience,
        maxChunks: retrieveMaxChunks,
      });

      setRetrieveChunks(result.chunks);
    } catch (retrieveLoadError) {
      setRetrieveChunks([]);
      setRetrieveError(retrieveLoadError instanceof Error ? retrieveLoadError.message : t('retrieveFailed', { ns: 'knowledge' }));
    } finally {
      setRetrieving(false);
    }
  };

  const onUseChunkInConversation = (chunk: KnowledgeRetrievedChunk) => {
    const prefillText = `[${chunk.documentName} • ${t('chunkIndex', { ns: 'knowledge', index: chunk.chunkIndex + 1 })}]\n${chunk.text}`;

    navigate('/conversations', {
      state: {
        prefillComposerText: prefillText,
        prefillComposerSource: 'knowledge-retrieve',
        prefillComposerNonce: Date.now(),
      },
    });
  };

  const onSubmitRetrievalFeedback = async (log: KnowledgeRetrievalLog, helpful: boolean) => {
    setSavingFeedbackLogId(log.id);

    try {
      const updated = await submitKnowledgeRetrievalFeedback(log.id, {
        helpful,
        feedback: feedbackDrafts[log.id] || '',
      });

      setRetrievalLogs(current => current.map(item => item.id === updated.id ? updated : item));
      showToast(t('retrievalFeedbackSaved', { ns: 'knowledge' }), 'success');
    } catch (saveError) {
      showToast(saveError instanceof Error ? saveError.message : t('retrievalFeedbackFailed', { ns: 'knowledge' }), 'error');
    } finally {
      setSavingFeedbackLogId('');
    }
  };

  return (
    <>
      <Breadcrumb items={breadcrumbItems} />

      <section>
        <div className={styles.pageHeader}>
          <div>
            <h1 className={styles.pageTitle}>{t('knowledgeBase', { ns: 'knowledge' })}</h1>
            <p className={styles.pageIntro}>{t('pageIntro', { ns: 'knowledge' })}</p>
            {urlQueryState.sourceFile ? (
              <p className={styles.pageIntro}>
                {t('openedFromCitation', {
                  ns: 'knowledge',
                  fileName: urlQueryState.sourceFile,
                  chunkSuffix: urlQueryState.sourceChunk ? `#${urlQueryState.sourceChunk}` : '',
                })}
              </p>
            ) : null}
          </div>
          <div className={styles.headerActions}>
            <button type="button" className="btn btn-secondary" onClick={onRefresh} disabled={loading}>
              {t('refresh', { ns: 'knowledge' })}
            </button>
          </div>
        </div>
      </section>

      <div className={styles.tabBar}>
        <button
          type="button"
          className={`${styles.tab} ${activeTab === 'documents' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('documents')}
        >
          {t('documentsTitle', { ns: 'knowledge' })}
        </button>
        <button
          type="button"
          className={`${styles.tab} ${activeTab === 'activity' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('activity')}
        >
          {t('activity.tab', { ns: 'knowledge' })}
        </button>
        <button
          type="button"
          className={`${styles.tab} ${activeTab === 'retrieve' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('retrieve')}
        >
          {t('retrieveTitle', { ns: 'knowledge' })}
        </button>
        {canManageKnowledge ? (
          <button
            type="button"
            className={`${styles.tab} ${activeTab === 'logs' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('logs')}
          >
            {t('retrievalLogsTitle', { ns: 'knowledge' })}
          </button>
        ) : null}
        {canViewOperations ? (
          <button
            type="button"
            className={`${styles.tab} ${activeTab === 'operations' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('operations')}
          >
            {t('ops.title', { ns: 'knowledge' })}
          </button>
        ) : null}
      </div>

      {activeTab === 'documents' ? (
        <>
          <section className="company-section" style={{ marginTop: 16 }}>
            <div className={styles.documentsSectionHeader}>
              <div>
                <h3>{t('documentsTitle', { ns: 'knowledge' })}</h3>
                <p className={styles.pageIntro}>
                  {canManageKnowledge
                    ? t('documentsIntroManage', { ns: 'knowledge' })
                    : t('attachments.managePermissionRequired', { ns: 'knowledge' })}
                </p>
              </div>
              <div className={styles.documentsSectionActions}>
                <KnowledgeDocumentsViewToggle value={documentsViewMode} onChange={changeDocumentsViewMode} />
                {canManageKnowledge ? (
                  <button type="button" className="btn btn-primary" onClick={() => setUploadPanelOpen(true)}>
                    {t('uploadTitle', { ns: 'knowledge' })}
                  </button>
                ) : null}
              </div>
            </div>

            <details className={styles.accessInfo}>
              <summary className={styles.accessInfoSummary}>
                <span className={styles.accessInfoIcon} aria-hidden="true">i</span>
                <span>{t('accessInfo.title', { ns: 'knowledge' })}</span>
              </summary>
              <div className={styles.accessInfoBody}>
                <p className={styles.accessInfoIntro}>{t('accessInfo.intro', { ns: 'knowledge' })}</p>
                <ul className={styles.accessInfoList}>
                  <li className={styles.accessInfoItem}>
                    <span className={`${styles.accessInfoBadge} ${styles.accessInfoBadgeInternal}`}>{t('internalOnly', { ns: 'knowledge' })}</span>
                    <span className={styles.accessInfoText}>{t('accessInfo.internal', { ns: 'knowledge' })}</span>
                  </li>
                  <li className={styles.accessInfoItem}>
                    <span className={`${styles.accessInfoBadge} ${styles.accessInfoBadgeCustomerSafe}`}>{t('customerSafe', { ns: 'knowledge' })}</span>
                    <span className={styles.accessInfoText}>{t('accessInfo.customerSafe', { ns: 'knowledge' })}</span>
                  </li>
                  <li className={styles.accessInfoItem}>
                    <span className={`${styles.accessInfoBadge} ${styles.accessInfoBadgePublic}`}>{t('visibleToAll', { ns: 'knowledge' })}</span>
                    <span className={styles.accessInfoText}>{t('accessInfo.public', { ns: 'knowledge' })}</span>
                  </li>
                </ul>
                <ul className={styles.accessInfoNotes}>
                  <li>{t('accessInfo.attachmentsNote', { ns: 'knowledge' })}</li>
                  <li>{t('accessInfo.restrictedNote', { ns: 'knowledge' })}</li>
                  <li>{t('accessInfo.structuredNote', { ns: 'knowledge' })}</li>
                  <li>{t('accessInfo.permissionsNote', { ns: 'knowledge' })}</li>
                </ul>
              </div>
            </details>

            <div className={styles.filtersRow}>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>{t('search', { ns: 'knowledge' })}</span>
                <input
                  className={styles.textField}
                  type="text"
                  value={searchDraft}
                  onChange={(event) => setSearchDraft(event.target.value)}
                  placeholder={t('searchPlaceholder', { ns: 'knowledge' })}
                />
              </label>

              <label className={styles.field}>
                <span className={styles.fieldLabel}>{t('visibility', { ns: 'knowledge' })}</span>
                <select
                  className={styles.selectField}
                  value={visibility}
                  onChange={(event) => { setVisibility(event.target.value as KnowledgeVisibility | 'all'); setPage(1); }}
                >
                  {visibilityOptions.map(option => (
                    <option key={option} value={option}>
                      {option === 'all'
                        ? t('allVisibility', { ns: 'knowledge' })
                        : option === 'customer_safe'
                          ? t('customerSafe', { ns: 'knowledge' })
                          : option === 'public'
                            ? t('visibleToAll', { ns: 'knowledge' })
                            : t('internalOnly', { ns: 'knowledge' })}
                    </option>
                  ))}
                </select>
              </label>

              <label className={styles.field}>
                <span className={styles.fieldLabel}>{t('statusLabel', { ns: 'knowledge' })}</span>
                <select
                  className={styles.selectField}
                  value={status}
                  onChange={(event) => { setStatus(event.target.value as KnowledgeProcessingStatus | 'all'); setPage(1); }}
                >
                  {processingStatuses.map(option => (
                    <option key={option} value={option}>
                      {option === 'all' ? t('allStatuses', { ns: 'knowledge' }) : t(`status.${option}`, { ns: 'knowledge' })}
                    </option>
                  ))}
                </select>
              </label>

              <label className={styles.field}>
                <span className={styles.fieldLabel}>{t('fileCategoryLabel', { ns: 'knowledge' })}</span>
                <select
                  className={styles.selectField}
                  value={fileCategoryFilter}
                  onChange={(event) => { setFileCategoryFilter(event.target.value); setPage(1); }}
                >
                  <option value="all">{t('allCategories', { ns: 'knowledge' })}</option>
                  <option value={KNOWLEDGE_UNCATEGORIZED_FILE_CATEGORY}>{t('noCategory', { ns: 'knowledge' })}</option>
                  {dataCategories.map(category => (
                    <option key={category.categoryId} value={category.categoryId}>
                      {category.displayName}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {accessDocument ? (
              <KnowledgeDocumentAccessModal
                key={accessDocument.id}
                document={accessDocument}
                canManage={canManageKnowledge}
                onClose={() => setAccessDocument(null)}
                onSaved={onAccessSaved}
              />
            ) : null}

            {uploadPanelOpen && canManageKnowledge ? createPortal(
              <div
                className={styles.uploadModalOverlay}
                onMouseDown={(event) => {
                  if (event.target === event.currentTarget) {
                    closeUploadModal();
                  }
                }}
              >
                <div
                  ref={uploadDialogRef}
                  className={styles.uploadModal}
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="knowledge-upload-title"
                  tabIndex={-1}
                >
                  <div className={styles.uploadModalHeader}>
                    <div>
                      <h2 id="knowledge-upload-title" className={styles.uploadModalTitle}>
                        {t('uploadTitle', { ns: 'knowledge' })}
                      </h2>
                      <p className={styles.uploadModalSubtitle}>
                        {isStructuredJsonUpload
                          ? t('structuredUploadHelp', { ns: 'knowledge' })
                          : t('uploadHelp', { ns: 'knowledge' })}
                      </p>
                    </div>
                    <button
                      type="button"
                      className={styles.uploadModalClose}
                      aria-label={t('close', { ns: 'common' })}
                      onClick={closeUploadModal}
                      disabled={uploading}
                    >
                      ×
                    </button>
                  </div>

                  <div className={styles.uploadModalBody}>
                <div className={styles.uploadGrid}>
                <div className={`${styles.field} ${styles.fieldFullWidth}`}>
                  <span className={styles.fieldLabel}>{t('documentFile', { ns: 'knowledge' })}</span>
                  <div className={styles.filePicker}>
                    <input
                      ref={fileInputRef}
                      type="file"
                      className={styles.hiddenInput}
                      accept=".pdf,.doc,.docx,.xlsx,.pptx,.txt,.md,.csv,.html,.htm,.json,application/json"
                      onChange={(event) => {
                        const nextFile = event.target.files?.[0] ?? null;
                        setSelectedFile(nextFile);
                        if (!isJsonUploadFile(nextFile)) {
                          setUploadFileCategory('');
                        }
                        setUploadTitle(current => {
                          const trimmedCurrent = current.trim();
                          if (!trimmedCurrent || trimmedCurrent === getDefaultTitleFromFile(selectedFile)) {
                            return getDefaultTitleFromFile(nextFile);
                          }

                          return current;
                        });
                      }}
                    />
                    <button type="button" className="btn btn-secondary" onClick={() => fileInputRef.current?.click()}>
                      {t('chooseFile', { ns: 'knowledge' })}
                    </button>
                    <span className={`${styles.fileName} ${selectedFile ? '' : styles.fileNameEmpty}`}>
                      {selectedFile?.name || t('noFileChosen', { ns: 'knowledge' })}
                    </span>
                  </div>
                </div>

                <label className={styles.field}>
                  <span className={styles.fieldLabel}>{t('title', { ns: 'knowledge' })}</span>
                  <input
                    className={styles.textField}
                    type="text"
                    maxLength={256}
                    value={uploadTitle}
                    onChange={(event) => setUploadTitle(event.target.value)}
                    placeholder={t('titlePlaceholder', { ns: 'knowledge' })}
                  />
                </label>

                  <label className={styles.field}>
                    <span className={styles.fieldLabel}>{t('visibility', { ns: 'knowledge' })}</span>
                    <select
                      className={styles.selectField}
                      value={uploadVisibility}
                      onChange={(event) => setUploadVisibility(event.target.value as KnowledgeVisibility)}
                    >
                      <option value="internal">{t('internalOnly', { ns: 'knowledge' })}</option>
                      <option value="customer_safe">{t('customerSafe', { ns: 'knowledge' })}</option>
                      <option value="public">{t('visibleToAll', { ns: 'knowledge' })}</option>
                    </select>
                    <span className={styles.fieldHint}>
                      {uploadVisibility === 'internal'
                        ? t('accessInfo.internal', { ns: 'knowledge' })
                        : uploadVisibility === 'customer_safe'
                          ? t('accessInfo.customerSafe', { ns: 'knowledge' })
                          : t('accessInfo.public', { ns: 'knowledge' })}
                    </span>
                  </label>

                {isStructuredJsonUpload ? (
                  <label className={styles.field}>
                    <span className={styles.fieldLabel}>{t('fileCategoryLabel', { ns: 'knowledge' })}</span>
                    <select
                      className={styles.selectField}
                      value={uploadFileCategory}
                      onChange={(event) => setUploadFileCategory(event.target.value)}
                    >
                      <option value="">{t('fileCategoryPlaceholder', { ns: 'knowledge' })}</option>
                      {dataCategories.map(category => (
                        <option key={category.categoryId} value={category.categoryId}>
                          {category.displayName}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}

                <label className={`${styles.field} ${isStructuredJsonUpload ? '' : styles.fieldFullWidth}`}>
                  <span className={styles.fieldLabel}>{t('tags', { ns: 'knowledge' })}</span>
                  <input
                    className={styles.textField}
                    type="text"
                    value={uploadTags}
                    onChange={(event) => setUploadTags(event.target.value)}
                    placeholder={t('tagsPlaceholder', { ns: 'knowledge' })}
                  />
                </label>

                <label className={`${styles.field} ${styles.fieldFullWidth}`}>
                  <span className={styles.fieldLabel}>{t('summary', { ns: 'knowledge' })}</span>
                  <textarea
                    className={styles.textArea}
                    rows={3}
                    maxLength={2000}
                    value={uploadSummary}
                    onChange={(event) => setUploadSummary(event.target.value)}
                    placeholder={t('summaryPlaceholder', { ns: 'knowledge' })}
                  />
                </label>

                {canManageKnowledge && isStructuredJsonUpload ? (
                  <div className={`${styles.field} ${styles.fieldFullWidth}`}>
                    <p className={styles.uploadImagesHelp}>
                      {t('attachments.hiddenForStructuredJson', { ns: 'knowledge' })}
                    </p>
                  </div>
                ) : null}

                {canManageKnowledge && !isStructuredJsonUpload ? (
                  <div className={`${styles.field} ${styles.fieldFullWidth}`}>
                    <div className={styles.uploadImagesCard}>
                      <span className={styles.fieldLabel}>{t('attachments.uploadSectionTitle', { ns: 'knowledge' })}</span>
                      {!attachmentsFeatureEnabled ? (
                        <p className={styles.uploadImagesHelp}>
                          {t('attachments.enableInSettings', { ns: 'knowledge' })}
                        </p>
                      ) : (
                        <>
                          <p className={styles.uploadImagesHelp}>
                            {t('attachments.uploadSectionHelp', {
                              ns: 'knowledge',
                              count: effectiveAttachmentSettings.maxAttachmentsPerDocument ?? 20,
                              imageSize: formatFileSize(effectiveAttachmentSettings.maxAttachmentBytes ?? DEFAULT_KNOWLEDGE_MAX_IMAGE_BYTES),
                              videoSize: formatFileSize(effectiveAttachmentSettings.maxVideoAttachmentBytes ?? DEFAULT_KNOWLEDGE_MAX_VIDEO_BYTES),
                            })}
                          </p>
                          <input
                            ref={uploadImagesInputRef}
                            type="file"
                            multiple
                            accept={getKnowledgeAttachmentAccept(effectiveAttachmentSettings)}
                            className={styles.hiddenInput}
                            onChange={(event) => {
                              const nextFiles = Array.from(event.target.files ?? []);
                              event.target.value = '';
                              addPendingUploadImages(nextFiles);
                            }}
                          />
                          <div
                            className={`${styles.uploadImagesDropZone} ${isUploadImagesDropZoneActive ? styles.uploadImagesDropZoneActive : ''}`}
                            onDragOver={(event) => {
                              event.preventDefault();
                              if (!uploading) {
                                setIsUploadImagesDropZoneActive(true);
                              }
                            }}
                            onDragLeave={() => setIsUploadImagesDropZoneActive(false)}
                            onDrop={(event) => {
                              event.preventDefault();
                              setIsUploadImagesDropZoneActive(false);
                              if (uploading) {
                                return;
                              }

                              addPendingUploadImages(Array.from(event.dataTransfer.files ?? []));
                            }}
                          >
                            {t('attachments.dropZone', { ns: 'knowledge' })}
                          </div>
                          <div className={styles.uploadImagesActions}>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => uploadImagesInputRef.current?.click()}
                              disabled={uploading}
                            >
                              {t('attachments.addImages', { ns: 'knowledge' })}
                            </button>
                            {pendingUploadImages.length > 0 ? (
                              <span className={styles.uploadImagesCount}>
                                {t('attachments.pendingCount', { ns: 'knowledge', count: pendingUploadImages.length })}
                              </span>
                            ) : null}
                          </div>
                          {pendingUploadImages.length > 0 ? (
                            <div className={styles.uploadImagesPreviewGrid}>
                              {pendingUploadImages.map((file, index) => (
                                <article key={`${file.name}-${file.lastModified}-${index}`} className={styles.uploadImagesPreviewCard}>
                                  {isKnowledgeVideoAttachment(file.type, file.name) ? (
                                    <video src={pendingImagePreviewUrls[index]} className={`${styles.uploadImagesPreview} ${styles.uploadImagesPreviewVideo}`} controls preload="metadata" />
                                  ) : (
                                    <img src={pendingImagePreviewUrls[index]} alt={file.name} className={styles.uploadImagesPreview} />
                                  )}
                                  <div className={styles.uploadImagesPreviewMeta}>
                                    <span>{file.name}</span>
                                    <button
                                      type="button"
                                      className={styles.inlineLinkButton}
                                      onClick={() => setPendingUploadImages(current => current.filter((_, itemIndex) => itemIndex !== index))}
                                      disabled={uploading}
                                    >
                                      {t('remove', { ns: 'common' })}
                                    </button>
                                  </div>
                                </article>
                              ))}
                            </div>
                          ) : null}
                        </>
                      )}
                    </div>
                  </div>
                ) : null}

                </div>
                  </div>

                  <div className={styles.uploadModalFooter}>
                    <button type="button" className="btn btn-secondary" onClick={closeUploadModal} disabled={uploading}>
                      {t('cancel', { ns: 'common' })}
                    </button>
                    <button type="button" className="btn btn-primary" onClick={onUpload} disabled={uploading}>
                      {uploading ? t('uploading', { ns: 'knowledge' }) : t('upload', { ns: 'knowledge' })}
                    </button>
                  </div>
                </div>
              </div>,
              document.body,
            ) : null}

        {error ? <div className={styles.error}>{error}</div> : null}

        {loading ? (
          <div style={{ marginTop: 20 }}>
            <Spinner text={t('loading', { ns: 'knowledge' })} />
          </div>
        ) : documents.length === 0 ? (
          <div className={styles.emptyStateCard}>
            <h4 className={styles.emptyStateTitle}>{t('emptyTitle', { ns: 'knowledge' })}</h4>
            <p className={styles.emptyStateText}>{t('empty', { ns: 'knowledge' })}</p>
            {canManageKnowledge ? (
              <button type="button" className="btn btn-primary" onClick={() => setUploadPanelOpen(true)}>
                {t('uploadTitle', { ns: 'knowledge' })}
              </button>
            ) : null}
          </div>
        ) : (
          <>
            {documentsViewMode === 'grid' ? (
              <KnowledgeDocumentGrid
                documents={documents}
                busyDocumentId={busyDocumentId}
                getCategoryLabel={getFileCategoryLabel}
                onOpenDetails={openDocumentDetails}
                onCopyLink={onCopyPublicUrl}
              />
            ) : documentsViewMode === 'list' ? (
              <KnowledgeDocumentTable
                documents={documents}
                busyDocumentId={busyDocumentId}
                getCategoryLabel={getFileCategoryLabel}
                onOpenDetails={openDocumentDetails}
                onCopyLink={onCopyPublicUrl}
              />
            ) : (
              <div className={styles.documentList}>
                {documents.map(document => (
                  <div
                    key={document.id}
                    id={`knowledge-document-${document.id}`}
                    className={focusedDocumentId === document.id ? styles.documentFocused : undefined}
                  >
                    <KnowledgeDocumentCard
                      document={document}
                      canManage={canManageKnowledge}
                      rowBusy={busyDocumentId === document.id}
                      attachmentsExpanded={expandedAttachmentsDocumentId === document.id}
                      categoryLabel={getFileCategoryLabel(document.fileCategory)}
                      editingTitleId={editingTitleId}
                      titleDraft={titleDraft}
                      editingTagsId={editingTagsId}
                      tagsDraft={tagsDraft}
                      editingSummaryId={editingSummaryId}
                      summaryDraft={summaryDraft}
                      onStartEditTitle={onStartEditTitle}
                      onSaveTitle={onSaveTitle}
                      onCancelEditTitle={onCancelEditTitle}
                      onTitleDraftChange={setTitleDraft}
                      onStartEditTags={onStartEditTags}
                      onSaveTags={onSaveTags}
                      onCancelEditTags={onCancelEditTags}
                      onTagsDraftChange={setTagsDraft}
                      onStartEditSummary={onStartEditSummary}
                      onSaveSummary={onSaveSummary}
                      onCancelEditSummary={onCancelEditSummary}
                      onSummaryDraftChange={setSummaryDraft}
                      onChangeVisibility={onChangeVisibility}
                      onOpenAccess={onOpenAccess}
                      onToggleAttachments={onToggleDocumentAttachments}
                      onReplaceFile={onReplaceFile}
                      onReprocess={onReprocess}
                      onDelete={onDelete}
                      onViewActivity={onViewFileActivity}
                      onCopyPublicUrl={onCopyPublicUrl}
                      onAttachmentCountChange={onAttachmentCountChange}
                    />
                  </div>
                ))}
              </div>
            )}

            <div className={styles.pager}>
              <span>{t('resultsSummary', { ns: 'knowledge', shown: documents.length, total: totalCount })}</span>
              <div className={styles.pagerButtons}>
                <button type="button" className="btn btn-secondary" disabled={page <= 1} onClick={() => setPage(current => Math.max(1, current - 1))}>
                  {t('previous', { ns: 'knowledge' })}
                </button>
                <span>{t('pageOf', { ns: 'knowledge', page, totalPages })}</span>
                <button type="button" className="btn btn-secondary" disabled={page >= totalPages} onClick={() => setPage(current => Math.min(totalPages, current + 1))}>
                  {t('next', { ns: 'knowledge' })}
                </button>
              </div>
            </div>
          </>
        )}
      </section>
        </>
      ) : null}

      {activeTab === 'retrieve' ? (
        <section className="company-section" style={{ marginTop: 16 }}>
          <h3 className={styles.sectionHeading}>{t('retrieveTitle', { ns: 'knowledge' })}</h3>
          <p className={styles.pageIntro}>{t('retrieveHelp', { ns: 'knowledge' })}</p>

          <form className={styles.retrieveForm} onSubmit={onRetrieve}>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('retrieveQueryLabel', { ns: 'knowledge' })}</span>
              <input
                className={styles.textField}
                type="text"
                value={retrieveQuery}
                onChange={(event) => setRetrieveQuery(event.target.value)}
                placeholder={t('retrieveQueryPlaceholder', { ns: 'knowledge' })}
              />
            </label>

            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('retrieveAudienceLabel', { ns: 'knowledge' })}</span>
              <select
                className={styles.selectField}
                value={retrieveAudience}
                onChange={(event) => setRetrieveAudience(event.target.value as 'employee' | 'customer')}
              >
                <option value="employee">{t('retrieveAudienceEmployee', { ns: 'knowledge' })}</option>
                <option value="customer">{t('retrieveAudienceCustomer', { ns: 'knowledge' })}</option>
              </select>
            </label>

            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('retrieveMaxChunksLabel', { ns: 'knowledge' })}</span>
              <select
                className={styles.selectField}
                value={retrieveMaxChunks}
                onChange={(event) => setRetrieveMaxChunks(Number(event.target.value))}
              >
                <option value={3}>3</option>
                <option value={5}>5</option>
                <option value={8}>8</option>
                <option value={10}>10</option>
              </select>
            </label>

            <button type="submit" className="btn btn-primary" disabled={retrieving}>
              {retrieving ? t('retrieving', { ns: 'knowledge' }) : t('retrieveRun', { ns: 'knowledge' })}
            </button>
          </form>

          {retrieveError ? <div className={styles.error}>{retrieveError}</div> : null}

          {retrieveChunks.length > 0 ? (
            <div className={styles.retrieveResults}>
              {retrieveChunks.map(chunk => (
                <article key={chunk.chunkId} className={styles.retrieveCard}>
                  <div className={styles.retrieveCardHead}>
                    <span className={styles.retrieveCardTitle}>{chunk.documentName}</span>
                    <div className={styles.actions}>
                      <span className={styles.retrieveCardMeta}>{t('chunkIndex', { ns: 'knowledge', index: chunk.chunkIndex + 1 })}</span>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => onUseChunkInConversation(chunk)}
                      >
                        {t('useInConversation', { ns: 'knowledge' })}
                      </button>
                    </div>
                  </div>
                  <div className={styles.retrieveCardBody}>{chunk.text}</div>
                </article>
              ))}
            </div>
          ) : null}

          {!retrieving && !retrieveError && retrieveQuery.trim() && retrieveChunks.length === 0 ? (
            <div className={styles.emptyState}>{t('retrieveEmpty', { ns: 'knowledge' })}</div>
          ) : null}
        </section>
      ) : null}

      {activeTab === 'activity' ? (
        <section className="company-section" style={{ marginTop: 16 }}>
          <div className={styles.sectionHead}>
            <div>
              <h3 className={styles.sectionHeading}>{t('activity.title', { ns: 'knowledge' })}</h3>
              <p className={styles.pageIntro}>{t('activity.help', { ns: 'knowledge' })}</p>
              {fileActivityDocumentId ? (
                <p className={styles.pageIntro}>
                  {t('activity.filteredBy', { ns: 'knowledge', name: fileActivityDocumentLabel || fileActivityDocumentId })}
                  {' '}
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setFileActivityDocumentId('');
                      setFileActivityDocumentLabel('');
                      setFileActivityPage(1);
                    }}
                  >
                    {t('activity.clearFilter', { ns: 'knowledge' })}
                  </button>
                </p>
              ) : null}
            </div>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('activity.actionLabel', { ns: 'knowledge' })}</span>
              <select
                className={styles.selectField}
                value={fileActivityAction}
                onChange={(event) => {
                  setFileActivityPage(1);
                  setFileActivityAction(event.target.value as 'all' | 'created' | 'updated' | 'deleted');
                }}
              >
                <option value="all">{t('activity.allActions', { ns: 'knowledge' })}</option>
                <option value="created">{t('activity.created', { ns: 'knowledge' })}</option>
                <option value="updated">{t('activity.updated', { ns: 'knowledge' })}</option>
                <option value="deleted">{t('activity.deleted', { ns: 'knowledge' })}</option>
              </select>
            </label>
          </div>
          {fileActivityError ? <div className={styles.error}>{fileActivityError}</div> : null}
          {fileActivityLoading ? (
            <Spinner text={t('activity.loading', { ns: 'knowledge' })} />
          ) : fileActivity.length === 0 ? (
            <div className={styles.emptyState}>{t('activity.empty', { ns: 'knowledge' })}</div>
          ) : (
            <div className={styles.logList}>
              {fileActivity.map((item) => {
                const actor = item.isSystem
                  ? t('activity.system', { ns: 'knowledge' })
                  : item.actorName || t('activity.unknown', { ns: 'knowledge' });
                const detail = item.detail || 'file';
                const label = t(`activity.events.${item.action}.${detail}`, {
                  ns: 'knowledge',
                  defaultValue: item.action,
                });
                const badgeClass = item.action === 'created'
                  ? styles.activityCreated
                  : item.action === 'deleted'
                    ? styles.activityDeleted
                    : styles.activityUpdated;
                return (
                  <article key={item.id} className={styles.logCard}>
                    <div className={styles.logCardHead}>
                      <div className={styles.logCardTitleRow}>
                        <span className={`${styles.activityAction} ${badgeClass}`}>{label}</span>
                        <span className={styles.activityFileName}>{item.entityLabel || item.entityId}</span>
                      </div>
                      <span className={styles.logMetaText}>{formatDateTime(item.createdAt)}</span>
                    </div>
                    <div className={styles.logMetaText}>{actor}</div>
                  </article>
                );
              })}
            </div>
          )}
          <div className={styles.pager}>
            <span>{t('resultsSummary', { ns: 'knowledge', shown: fileActivity.length, total: fileActivityTotalCount })}</span>
            <div className={styles.pagerButtons}>
              <button type="button" className="btn btn-secondary" disabled={fileActivityPage <= 1} onClick={() => setFileActivityPage((current) => Math.max(1, current - 1))}>
                {t('previous', { ns: 'knowledge' })}
              </button>
              <span>{t('pageOf', { ns: 'knowledge', page: fileActivityPage, totalPages: fileActivityTotalPages })}</span>
              <button type="button" className="btn btn-secondary" disabled={fileActivityPage >= fileActivityTotalPages} onClick={() => setFileActivityPage((current) => Math.min(fileActivityTotalPages, current + 1))}>
                {t('next', { ns: 'knowledge' })}
              </button>
            </div>
          </div>
        </section>
      ) : null}

      {activeTab === 'logs' && canManageKnowledge ? (
        <section className="company-section" style={{ marginTop: 16 }}>
          <div className={styles.sectionHead}>
            <div>
              <h3 className={styles.sectionHeading}>{t('retrievalLogsTitle', { ns: 'knowledge' })}</h3>
              <p className={styles.pageIntro}>{t('retrievalLogsHelp', { ns: 'knowledge' })}</p>
            </div>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('retrievalContextLabel', { ns: 'knowledge' })}</span>
              <select
                className={styles.selectField}
                value={retrievalContextFilter}
                onChange={(event) => { setRetrievalContextFilter(event.target.value as KnowledgeRetrievalContextType | 'all'); setRetrievalLogsPage(1); }}
              >
                {retrievalContextOptions.map(option => (
                  <option key={option} value={option}>
                    {option === 'all' ? t('allContexts', { ns: 'knowledge' }) : formatContextType(option)}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {retrievalLogsError ? <div className={styles.error}>{retrievalLogsError}</div> : null}

          {retrievalLogsLoading ? (
            <div style={{ marginTop: 20 }}>
              <Spinner text={t('retrievalLogsLoading', { ns: 'knowledge' })} />
            </div>
          ) : retrievalLogs.length === 0 ? (
            <div className={styles.emptyState}>{t('retrievalLogsEmpty', { ns: 'knowledge' })}</div>
          ) : (
            <div className={styles.logList}>
              {retrievalLogs.map(log => {
                const isSavingFeedback = savingFeedbackLogId === log.id;

                return (
                  <article key={log.id} className={styles.logCard}>
                    <div className={styles.logCardHead}>
                      <div className={styles.logCardTitleRow}>
                        <span className={styles.logContextBadge}>{formatContextType(log.contextType)}</span>
                        <span className={styles.logMetaText}>{formatDateTime(log.createdAt)}</span>
                      </div>
                      <div className={styles.logCardTitleRow}>
                        <span className={styles.logMetaText}>{t('retrievalMethodValue', { ns: 'knowledge', method: log.retrievalMethod || 'unknown' })}</span>
                        <span className={styles.logMetaText}>{t('retrievalChunkCountValue', { ns: 'knowledge', count: log.retrievedChunkCount })}</span>
                        <span className={styles.logMetaText}>{t('retrievalLatencyValue', { ns: 'knowledge', latency: log.latencyMilliseconds })}</span>
                      </div>
                    </div>

                    <div className={styles.logQuery}>{log.query || t('retrievalQueryMissing', { ns: 'knowledge' })}</div>

                    <div className={styles.logMetaGrid}>
                      <div>
                        <span className={styles.fieldLabel}>{t('retrievalRelevanceLabel', { ns: 'knowledge' })}</span>
                        <div className={styles.logMetaValue}>{log.averageRelevanceScore != null ? log.averageRelevanceScore.toFixed(3) : '—'}</div>
                      </div>
                      <div>
                        <span className={styles.fieldLabel}>{t('retrievalEmbeddingLabel', { ns: 'knowledge' })}</span>
                        <div className={styles.logMetaValue}>{log.embeddingModel || '—'}</div>
                      </div>
                      <div>
                        <span className={styles.fieldLabel}>{t('retrievalConversationLabel', { ns: 'knowledge' })}</span>
                        <div className={styles.logMetaValue}>
                          {log.conversationId
                            ? (
                              <Link to={`/conversations/${log.conversationId}`} className={styles.conversationLink}>
                                {log.conversationTitle || log.conversationId}
                              </Link>
                            )
                            : '—'}
                        </div>
                      </div>
                    </div>

                    <div className={styles.feedbackBlock}>
                      <div className={styles.feedbackButtons}>
                        <button
                          type="button"
                          className={`btn btn-secondary ${log.userFoundHelpful === true ? styles.feedbackPositive : ''}`}
                          onClick={() => void onSubmitRetrievalFeedback(log, true)}
                          disabled={isSavingFeedback}
                        >
                          {t('retrievalHelpful', { ns: 'knowledge' })}
                        </button>
                        <button
                          type="button"
                          className={`btn btn-secondary ${log.userFoundHelpful === false ? styles.feedbackNegative : ''}`}
                          onClick={() => void onSubmitRetrievalFeedback(log, false)}
                          disabled={isSavingFeedback}
                        >
                          {t('retrievalNotHelpful', { ns: 'knowledge' })}
                        </button>
                      </div>

                      <textarea
                        className={styles.feedbackInput}
                        value={feedbackDrafts[log.id] ?? log.feedback ?? ''}
                        onChange={(event) => setFeedbackDrafts(current => ({ ...current, [log.id]: event.target.value }))}
                        placeholder={t('retrievalFeedbackPlaceholder', { ns: 'knowledge' })}
                        rows={2}
                      />
                    </div>
                  </article>
                );
              })}

              <div className={styles.pager}>
                <span>{t('resultsSummary', { ns: 'knowledge', shown: retrievalLogs.length, total: retrievalLogsTotalCount })}</span>
                <div className={styles.pagerButtons}>
                  <button type="button" className="btn btn-secondary" disabled={retrievalLogsPage <= 1} onClick={() => setRetrievalLogsPage(current => Math.max(1, current - 1))}>
                    {t('previous', { ns: 'knowledge' })}
                  </button>
                  <span>{t('pageOf', { ns: 'knowledge', page: retrievalLogsPage, totalPages: retrievalLogTotalPages })}</span>
                  <button type="button" className="btn btn-secondary" disabled={retrievalLogsPage >= retrievalLogTotalPages} onClick={() => setRetrievalLogsPage(current => Math.min(retrievalLogTotalPages, current + 1))}>
                    {t('next', { ns: 'knowledge' })}
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>
      ) : null}

      {activeTab === 'operations' && canViewOperations ? (
        <div className={styles.adminTopGrid} style={{ marginTop: 16 }}>
          {/* Left column stacks the snapshot and evaluation so the tall settings panel never pushes evaluation below the fold. */}
          <div className={styles.adminColumn}>
            <KnowledgeBaseOperationalSummaryPanel />
            {canManageKnowledge ? <KnowledgeEvaluationPanel /> : null}
          </div>
          <div className={styles.adminColumn}>
            <KnowledgeBaseSettingsPanel />
          </div>
        </div>
      ) : null}

    </>
  );
};

export default KnowledgeBasePage;